import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X, Crown, Cloud, Clock } from 'lucide-react';
import { MEMBERSHIP_UI_VISIBLE } from '@/config/membership';

interface PaywallModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * 会员付费引导弹窗：聊天命中「外部模型为 Pro 会员功能」403 时弹出。
 * 「去开通」跳会员页，「绑定云端会员账号」跳 AI 设置页，「稍后再说」关闭。
 */
const PaywallModal: FC<PaywallModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  if (!open || !MEMBERSHIP_UI_VISIBLE) return null;

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 20 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        className="relative z-10 w-full max-w-[420px] mx-4"
      >
        <div className="glass-card p-8">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 p-1.5 rounded-[2px] text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-12 h-12 rounded-[2px] bg-warning/10 flex items-center justify-center mb-4">
              <Crown className="w-6 h-6 text-warning" />
            </div>
            <h2 className="text-lg font-semibold text-text-primary mb-2">外部模型为 Pro 会员功能</h2>
            <p className="text-sm text-text-muted leading-relaxed">
              当前选择的外部模型需要开通 Pro 会员（¥9.9/月）才能使用。
              开通后填自己的 API Key 即可调用，token 用量由厂商直接结算，平台不另收费。
              本地 Ollama 模型不受影响，可继续免费使用。
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => go('/settings/ai')}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              <Crown size={16} />
              去开通（¥9.9/月，云端下单）
            </button>
            <button
              onClick={() => go('/settings/ai')}
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              <Cloud size={16} />
              绑定云端会员账号
            </button>
            <button
              onClick={onClose}
              className="w-full py-2 text-sm text-text-muted hover:text-text-primary transition-colors flex items-center justify-center gap-2"
            >
              <Clock size={14} />
              稍后再说
            </button>
          </div>

          <p className="mt-5 text-center text-xs text-text-muted">
            需要多人协作？{' '}
            <a
              href="https://grzhishiku.com/#pricing"
              target="_blank"
              rel="noreferrer"
              className="text-warning hover:underline"
            >
              了解企业版（规划中）
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default PaywallModal;
